<script setup lang="ts">
import type { PropType } from 'vue';

import type { FlatStudy } from '../store/dicom-web/current-study-store';
import type { OrthancSeriesInfo } from '@/src/core/orthanc-api';

import { useAppSettings } from '@src/composables/useAppSettings';
import { useAppState } from '@src/store/ocg-app-state';
import { format } from 'date-fns';
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useTemplateRef,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';

import LoadSaveAnalysisStateDialog from '@/src/components/LoadSaveAnalysisStateDialog.vue';
import { useGeneralPurposeOverlayManager } from '@/src/store/overlay-management/overlay-manager';

import { useActiveStudyStore } from '../store/active-study';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';
import { useOrthancStore } from '../store/dicom-web/orthanc-store';

import SquareLoader from './SquareLoader.vue';

const props = defineProps({
  study: {
    type: Object as PropType<FlatStudy | Record<string, any>>,
    required: true,
  },
});

const MODALITY_COLORS: Record<
  string,
  { accent: string; badgeBg: string; badgeText: string }
> = {
  MR: { accent: '#378ADD', badgeBg: '#E6F1FB', badgeText: '#0C447C' },
  CT: { accent: '#1D9E75', badgeBg: '#E1F5EE', badgeText: '#085041' },
  US: { accent: '#7F77DD', badgeBg: '#EEEDFE', badgeText: '#3C3489' },
  XR: { accent: '#BA7517', badgeBg: '#FAEEDA', badgeText: '#633806' },
  PT: { accent: '#D85A30', badgeBg: '#FAECE7', badgeText: '#712B13' },
  NM: { accent: '#D4537E', badgeBg: '#FBEAF0', badgeText: '#72243E' },
};

function formatDate(dateString?: string): string {
  if (!dateString) return '';
  try {
    if (/^\d{8}$/.test(dateString)) {
      return format(
        new Date(
          Number.parseInt(dateString.slice(0, 4), 10),
          Number.parseInt(dateString.slice(4, 6), 10) - 1,
          Number.parseInt(dateString.slice(6, 8), 10),
        ),
        'MMM dd, yyyy',
      );
    }
    const d = new Date(dateString);
    if (!Number.isNaN(d.getTime())) {
      return format(d, 'MMM dd, yyyy');
    }
    return dateString;
  }
  catch {
    return dateString;
  }
}

function formatTime(timeString?: string): string {
  if (!timeString || timeString.length < 4) return '';
  return `${timeString.slice(0, 2)}:${timeString.slice(2, 4)}`;
}

const patientName = computed(() => {
  const raw = props.study?.PatientName;
  if (!raw) return 'Unknown Patient';
  return raw.replace(/\^/g, ' ').trim();
});

const studyDescription = computed(() => {
  return props.study?.StudyDescription?.trim() || '';
});

const formattedDate = computed(() => formatDate(props.study?.StudyDate));
const formattedTime = computed(() => formatTime(props.study?.StudyTime));

const modalityStyle = computed(() => {
  const mod = props.study?.Modality?.toUpperCase();
  const c = (mod && MODALITY_COLORS[mod]) || {
    accent: '#888780',
    badgeBg: 'rgba(128, 128, 128, 0.16)',
    badgeText: 'var(--md-text-primary)',
  };
  return {
    background: c.badgeBg,
    color: c.badgeText,
  };
});

function flattenSeries(s: OrthancSeriesInfo) {
  return {
    id: s.ID,
    uid: s.MainDicomTags.SeriesInstanceUID,
    // eslint-disable-next-line dot-notation
    description: s.MainDicomTags['SeriesDescription'] || '',
    // eslint-disable-next-line dot-notation
    modality: s.MainDicomTags['Modality'] || '',
    // eslint-disable-next-line dot-notation
    seriesNumber: Number.parseInt(s.MainDicomTags['SeriesNumber'] || '0', 10),
    instanceCount: s.Instances.length,
    firstInstanceId: s.Instances[0] || '',
  };
}

const orthancStore = useOrthancStore();
const activeStudyStore = useActiveStudyStore();
const currentStudyStore = useCurrentStudyStore();
const router = useRouter();

const isLoading = ref(false);

const studySeriesInfo = computed(() => orthancStore.studySeries);
const seriesList = computed(() =>
  studySeriesInfo.value
    .map(flattenSeries)
    .sort((a, b) => a.seriesNumber - b.seriesNumber),
);

const thumbnails = reactive<Record<string, string>>({});

watch(
  () => orthancStore.studySeries,
  async (raw) => {
    const list = raw
      .map(flattenSeries)
      .sort((a, b) => a.seriesNumber - b.seriesNumber);
    if (!list.length) return;
    const BATCH_SIZE = 3;

    for (let i = 0; i < list.length; i += BATCH_SIZE) {
      const batch = list.slice(i, i + BATCH_SIZE);
      const results = await Promise.all(
        batch
          .filter(s => s.firstInstanceId && !thumbnails[s.id])
          .map(s =>
            orthancStore
              .fetchThumbnail(s.firstInstanceId)
              .then(url => ({ id: s.id, url })),
          ),
      );
      results.forEach(({ id, url }) => {
        if (url) thumbnails[id] = url;
      });
      await new Promise((r) => {
        setTimeout(r, 0);
      });
    }
  },
);

const loadSaveAnalysisStateDialog = useTemplateRef<
  InstanceType<typeof LoadSaveAnalysisStateDialog>
>('loadSaveAnalysisStateDialog');

onMounted(async () => {
  try {
    if (!props.study?.OrthancID) {
      console.error('Study missing OrthancID');
      return;
    }
    await orthancStore.fetchStudySeries(props.study.OrthancID);
    orthancStore.startSeriesPolling(10000);
  }
  catch (e) {
    console.error('Failed to load series/thumbnails:', e);
  }
});

onBeforeUnmount(() => {
  orthancStore.stopSeriesPolling();
});

const isStableStudy = computed(() => {
  const uid = props.study?.StudyInstanceUID;
  if (!uid) return false;
  const live = orthancStore.studies.find(
    s => s.MainDicomTags.StudyInstanceUID === uid,
  );
  return live?.IsStable ?? false;
});

const initCurrentStudy = async () => {
  try {
    if (!isStableStudy.value) {
      return;
    }
    isLoading.value = true;
    await currentStudyStore.initStudy(
      props.study as FlatStudy,
      studySeriesInfo.value,
    );
    activeStudyStore.initStudyData();

    const numSeries = currentStudyStore.getSerieCount();
    for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex++) {
      const sliceCount = currentStudyStore.getSliceCount(seriesIndex);
      for (let sliceIndex = 0; sliceIndex < sliceCount; sliceIndex++) {
        const numImages = currentStudyStore.getImagesCount(
          seriesIndex,
          sliceIndex,
        );
        activeStudyStore.addStudyData(
          seriesIndex.toString(),
          seriesIndex,
          sliceIndex,
          numImages,
        );
      }
    }

    activeStudyStore.setActive(props.study.StudyInstanceUID);
    activeStudyStore.setPatientInfo(props.study.StudyInstanceUID);

    const studyID = currentStudyStore.getStudyID;
    loadSaveAnalysisStateDialog.value?.loadLatestSave(studyID);
    const { applyAll } = useAppSettings();
    applyAll();
    useAppState().setView('QualityCheck');
    const overlayManager = useGeneralPurposeOverlayManager();
    overlayManager.showGeneralPurposeOverlay({ viewerType: 'viewer' });

    await router.push('/analysis');
  }
  catch (error) {
    console.error('Failed to initialize current study:', error);
  }
  finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col overflow-hidden h-full min-h-0"
    :class="{ 'study-not-stable': !isStableStudy }"
  >
    <!-- Selected Study Info & Load Row -->
    <div class="selected-study-bar">
      <div class="study-info-col">
        <!-- Eyebrow: Title / Modality / Date / Receiving status -->
        <div class="study-eyebrow-row">
          <span class="">
            {{ patientName }}

          </span>
          <span
            v-if="props.study?.Modality"
            class="modality-badge"
            :style="modalityStyle"
          >
            {{ props.study.Modality }}
          </span>
          <span v-if="formattedDate" class="study-date-chip">
            {{ formattedDate }}
            <span v-if="formattedTime" class="study-time">
              ·  {{ formattedTime }}
            </span>
          </span>
          <span v-if="!isStableStudy" class="receiving-badge">
            <span class="receiving-pulse" />
            Receiving…
          </span>
        </div>
        <!-- Description Below -->
        <div class="study-desc-row">
          <span
            class="study-desc"
            :class="{ missing: !studyDescription }"
            :title="studyDescription || 'No description available'"
          >
            {{ studyDescription || 'No description available' }}
          </span>
          <span v-if="seriesList.length" class="series-count">
            · {{ seriesList.length }} {{ seriesList.length === 1 ? 'series' : 'series' }}
          </span>
        </div>
      </div>

      <!-- Action Button -->
      <div class="study-action-col">
        <button
          class="button btn-primary load-study-btn"
          :disabled="isLoading || !isStableStudy"
          :title="!isStableStudy ? 'Study is still receiving data…' : 'Load Study'"
          @click="initCurrentStudy"
        >
          <i v-if="isLoading" class="fa-solid fa-circle-notch fa-spin mr-1.5" />
          <span>{{ isLoading ? 'Loading…' : 'Load Study' }}</span>
        </button>
      </div>
    </div>

    <div class="thumbnail-viewer-container flex-grow min-h-0 overflow-y-auto">
      <div
        v-if="isLoading"
        class="mt-10 mx-auto flex flex-col items-center cursor-progress"
      >
        <v-progress-circular indeterminate />
        Loading Study...
      </div>
      <div v-else class="flex flex-wrap gap-2">
        <div
          v-for="series in seriesList"
          :key="series.id"
          class="small-thumbnail-image-container"
        >
          <div class="small-thumbnail-image">
            <img
              v-if="thumbnails[series.id]"
              :src="thumbnails[series.id]"
              class="w-full h-full object-cover"
              :alt="series.description"
            >
            <SquareLoader v-else />
            <div class="overlay-thumbnail-tag">
              <p>{{ series.instanceCount }}</p>
              <i class="fa-solid fa-images fa-sm" />
            </div>
          </div>
          <div class="break-words" style="width: var(--small-thumbnail-width)">
            <p>{{ series.description || '(no series description)' }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <LoadSaveAnalysisStateDialog ref="loadSaveAnalysisStateDialog" />
</template>

<style scoped>
.selected-study-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 10px 14px;
  margin-bottom: 14px;
  background: var(--md-surface-container-low);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 8px;
  flex-shrink: 0;
}

.study-info-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.study-eyebrow-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.study-section-title {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--md-primary);
  display: inline-flex;
  align-items: center;
}

.modality-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 4px;
  letter-spacing: 0.04em;
  line-height: 1.2;
}

.study-date-chip {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  color: var(--md-text-secondary);
  background: var(--md-surface-container-high);
  border: 1px solid var(--md-outline-variant);
  border-radius: 99px;
  padding: 1px 8px;
  white-space: nowrap;
  line-height: 1.2;
}

.study-time {
  opacity: 0.7;
  margin-left: 4px;
}

.receiving-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.68rem;
  font-weight: 600;
  color: #ff9800;
  background: rgba(255, 152, 0, 0.12);
  border: 1px solid rgba(255, 152, 0, 0.3);
  padding: 1px 7px;
  border-radius: 99px;
  line-height: 1.2;
}

.receiving-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff9800;
  animation: pulseDot 1.4s ease-in-out infinite;
}

@keyframes pulseDot {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}

.patient-name {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--md-text-primary);
  line-height: 1.25;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.study-desc-row {
  display: flex;
  align-items: center;
  font-size: 0.825rem;
  color: var(--md-text-secondary);
  line-height: 1.3;
  min-width: 0;
}

.study-desc {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.study-desc.missing {
  font-style: italic;
  opacity: 0.6;
}

.series-count {
  flex-shrink: 0;
  opacity: 0.85;
  margin-left: 4px;
}

.study-action-col {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.load-study-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.45rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  color: var(--md-text-primary);
}

.load-study-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  pointer-events: none;
  filter: grayscale(40%);
}

.thumbnail-viewer-container::-webkit-scrollbar {
  display: none;
  width: 0 !important;
}

.active {
  background: var(--md-primary);
}

.thumbnail-skeleton {
  width: var(--small-thumbnail-width, 120px);
  height: var(--small-thumbnail-width, 120px);
  background: var(--md-bg);
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
}

.study-not-stable {
  opacity: 0.45;
  pointer-events: none;
  position: relative;
}
</style>
